// R1e — A2P 10DLC-compliant Terms & Conditions (canonical route /terms-and-conditions).
// R1-VISUAL Slice 7b — Style-only reskin. All copy (STOP / HELP / rates /
// frequency / non-condition clauses) preserved verbatim.
// Dedicated SMS/Messaging Terms section is mandated by Twilio Trust Hub review.
import React from "react";
import { Link } from "react-router-dom";
import PublicShell, { usePublicContent } from "../../components/public/PublicShell";

const TERMS_VERSION = "v1.0";
const EFFECTIVE = "September 23, 2026";

const LOGO = "/brand/erf_logo_horizontal.jpg";

const T = {
  ivory: "#F5F1E8",
  navy: "#0B2545",
  brass: "#B08D57",
  charcoal: "#1F2937",
  border: "#E5E1DA",
  walnut: "#5B3A1F",
  bgAlt: "#FAF7F2",
};

function LegalH2({ id, children, testId }) {
  return (
    <>
      <div
        aria-hidden="true"
        style={{ height: 1, background: T.brass, opacity: 0.55, margin: "56px 0 24px" }}
      />
      <h2
        id={id}
        data-testid={testId}
        style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 28,
          fontWeight: 700,
          color: T.navy,
          margin: 0,
          letterSpacing: "-0.005em",
          lineHeight: 1.2,
        }}
      >
        {children}
      </h2>
    </>
  );
}

function LegalH3({ children, marginTop = 32 }) {
  return (
    <>
      <div
        aria-hidden="true"
        style={{ height: 1, background: T.brass, opacity: 0.4, marginTop, marginBottom: 14 }}
      />
      <h3
        style={{
          fontFamily: '"Playfair Display", Georgia, serif',
          fontSize: 20,
          fontWeight: 600,
          color: T.navy,
          margin: 0,
          lineHeight: 1.25,
        }}
      >
        {children}
      </h3>
    </>
  );
}

export default function TermsAndConditions() {
  const c = usePublicContent();
  const supportEmail = c.contact_email || "info@erfenterprisesllc.com";
  const supportOffice = c.contact_phone_office || "(800) 240-6404";
  const supportMobile = c.contact_phone_mobile || "(771) 271-3011";

  return (
    <PublicShell>
      {/* Navy eyebrow band — logo left, page title right. */}
      <section
        data-testid="terms-conditions-eyebrow-band"
        style={{ background: T.navy, color: T.ivory, borderBottom: `1px solid ${T.brass}` }}
      >
        <div
          className="mx-auto px-4 md:px-6 lg:px-8"
          style={{
            maxWidth: 1600,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 24,
            padding: "22px 24px",
            flexWrap: "wrap",
          }}
        >
          <Link
            to="/"
            aria-label="ERF Enterprises home"
            data-testid="terms-eyebrow-logo-link"
            style={{ display: "inline-flex", alignItems: "center" }}
          >
            <img
              src={LOGO}
              alt="ERF ENTERPRISES LLC"
              style={{ height: 40, width: "auto", display: "block", background: T.ivory, padding: "4px 10px", borderRadius: 3 }}
            />
          </Link>
          <div
            style={{
              fontFamily: '"IBM Plex Mono", monospace',
              fontSize: 11,
              letterSpacing: "0.28em",
              textTransform: "uppercase",
              color: T.brass,
            }}
          >
            Legal · Terms &amp; Conditions
          </div>
        </div>
      </section>

      <section
        data-testid="terms-conditions-body"
        style={{ background: T.ivory, color: T.charcoal }}
      >
        <div
          className="mx-auto px-4 md:px-6 lg:px-8"
          style={{ maxWidth: 1600, padding: "64px 24px 80px" }}
        >
          <div style={{ maxWidth: "72ch", margin: "0 auto" }}>
            <div
              style={{
                fontFamily: '"IBM Plex Mono", monospace',
                fontSize: 11,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: T.brass,
              }}
            >
              Legal
            </div>
            <h1
              style={{
                marginTop: 14,
                fontFamily: '"Playfair Display", Georgia, serif',
                fontSize: "clamp(34px, 4.4vw, 46px)",
                fontWeight: 700,
                color: T.navy,
                letterSpacing: "-0.01em",
                lineHeight: 1.12,
              }}
            >
              Terms &amp; Conditions
            </h1>
            <p style={{ fontSize: 13, color: T.charcoal, opacity: 0.72, marginTop: 10 }}>
              Version {TERMS_VERSION} · Effective {EFFECTIVE}
            </p>

            <div style={{ fontSize: 16, lineHeight: 1.75, color: T.charcoal, marginTop: 28 }}>
              <p>
                These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your use of this website and the services provided
                by ERF ENTERPRISES LLC (&ldquo;ERF,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;). By accessing this site,
                submitting a form, or engaging our services, you agree to these Terms.
              </p>

              <LegalH3>Services</LegalH3>
              <p>
                ERF provides general contracting services on a per-project basis under a signed written contract. Website
                content is informational only and does not constitute a binding offer. Quotes and estimates provided via
                this site or in initial consultation are non-binding until formalized in a signed contract.
              </p>

              <LegalH3>Intellectual property</LegalH3>
              <p>
                All content on this site — text, imagery, logos — is the property of ERF ENTERPRISES LLC and may not be
                reused without written permission.
              </p>

              <LegalH3>Limitation of liability</LegalH3>
              <p>
                ERF&rsquo;s total liability for any claim arising from this site is limited to the amount, if any, paid
                for use of this site. Construction services are governed exclusively by the signed project contract.
              </p>

              {/* ========================================================
                   SMS / Messaging Terms — Twilio A2P 10DLC required section
                   Do not modify the disclosures below without a compliance
                   review. Version pinned; changes bump `terms_version`.
                ========================================================= */}
              <LegalH2 id="sms-messaging-terms" testId="sms-messaging-terms-heading">
                SMS / Messaging Terms
              </LegalH2>

              <LegalH3 marginTop={24}>Program &amp; brand</LegalH3>
              <p>
                The SMS program is operated by <strong>ERF ENTERPRISES LLC</strong> under its own brand.
              </p>

              <LegalH3 marginTop={24}>Program description</LegalH3>
              <p>
                Low-volume customer, project, service, account, scheduling, estimate/quote, and operational communications
                relating to your inquiries and active engagements with ERF.
              </p>

              <LegalH3 marginTop={24}>Message frequency</LegalH3>
              <p>Message frequency varies based on the activity in your project or inquiry (message frequency varies).</p>

              <LegalH3 marginTop={24}>Cost</LegalH3>
              <p><strong>Message and data rates may apply.</strong> Charges are set by your mobile carrier, not by ERF.</p>

              <LegalH3 marginTop={24}>Carriers</LegalH3>
              <p>
                Mobile carriers are not liable for delayed or undelivered messages to the extent permitted by applicable
                law. Delivery of any given message depends on your carrier&rsquo;s network conditions.
              </p>

              <LegalH3 marginTop={24}>Opt out / help</LegalH3>
              <p>
                You can opt out of the SMS program at any time by replying <strong>STOP</strong>, <strong>STOPALL</strong>,
                <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>, <strong>END</strong>, or <strong>QUIT</strong> to any
                message you have received from us. After you opt out, you will not receive further SMS from ERF unless you
                re-enroll through the website opt-in form. For assistance, reply <strong>HELP</strong> or contact us using
                the details below. Reply STOP to opt out. Reply HELP for support.
              </p>

              <LegalH3 marginTop={24}>Consent is not a condition of purchase</LegalH3>
              <blockquote
                data-testid="sms-consent-not-required-clause"
                style={{
                  borderLeft: `3px solid ${T.brass}`,
                  background: T.bgAlt,
                  padding: "16px 20px",
                  borderRadius: 3,
                  marginTop: 6,
                  marginBottom: 0,
                  color: T.walnut,
                  fontFamily: '"Playfair Display", Georgia, serif',
                  fontStyle: "italic",
                  fontSize: 16.5,
                  lineHeight: 1.6,
                }}
              >
                Consent to receive SMS is <strong>not</strong> a condition of purchase or of any ERF service. You may
                decline SMS consent and still submit quote, contact, or service requests through email, phone, or the
                contact form on our website.
              </blockquote>

              <LegalH3 marginTop={24}>Privacy</LegalH3>
              <p>
                How ERF handles your phone number and SMS opt-in data is described in our{" "}
                <Link to="/privacy-policy" data-testid="terms-link-privacy" style={{ color: T.navy, textDecoration: "underline" }}>Privacy Policy</Link>. Mobile information and
                SMS opt-in/consent data will not be sold or shared with third parties or affiliates for their marketing or
                promotional purposes.
              </p>

              <LegalH3 marginTop={24}>Support</LegalH3>
              <ul style={{ paddingLeft: 22, marginTop: 8 }}>
                <li style={{ marginBottom: 6 }}>Email: <a href={`mailto:${supportEmail}`} style={{ color: T.navy, textDecoration: "underline" }}>{supportEmail}</a></li>
                <li style={{ marginBottom: 6 }}>Office phone: <a href={`tel:+1${supportOffice.replace(/\D/g, "").slice(-10)}`} style={{ color: T.navy, textDecoration: "underline" }}>{supportOffice}</a></li>
                <li>Mobile: <a href={`tel:+1${supportMobile.replace(/\D/g, "").slice(-10)}`} style={{ color: T.navy, textDecoration: "underline" }}>{supportMobile}</a></li>
              </ul>

              <LegalH3>Governing law</LegalH3>
              <p>
                These Terms are governed by the laws of the Commonwealth of Pennsylvania, without regard to conflict-of-law
                principles. Questions about these Terms may be directed to ERF via the contact information above.
              </p>

              <p style={{ marginTop: 48, fontSize: 12, color: T.charcoal, fontStyle: "italic", opacity: 0.7 }}>
                We may update these Terms from time to time. Material changes will be reflected by an updated version
                number and effective date at the top of this page.
              </p>
            </div>
          </div>
        </div>
      </section>
    </PublicShell>
  );
}
